import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Td,
  Textarea,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { FaStar, FaRegStar, FaRegStickyNote, FaRegCommentDots } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { PLATFORM_ROLES, BU_ROLES } from "../../../constants/roles";
import {
  listMonthlyRatings,
  createMonthlyRating,
  updateMonthlyRating,
  deleteMonthlyRating,
  listAnnualReviews,
  createAnnualReview,
  updateAnnualReview,
  deleteAnnualReview,
} from "../../../api/consultantsApi";
import ConfirmDialog from "../../../components/Portal/ConfirmDialog";
import {
  Panel,
  DataTable,
  StatusBadge,
  MiniIconButton,
  EmptyState,
  FieldLabel,
} from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MONTH_NAMES_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_NAMES_FR = ["Janv.", "Févr.", "Mars", "Avr.", "Mai", "Juin", "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc."];

const REVIEW_TYPES = ["annual", "salary", "client_feedback"];

// Annual review type -> StatusBadge colorScheme (registered theme scales only).
const REVIEW_TYPE_SCHEME = {
  annual: "blue",
  salary: "green",
  client_feedback: "orange",
};

const StarRating = ({ value, onChange, readOnly = false }) => {
  return (
    <HStack spacing={1}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Box
          key={star}
          as="button"
          type="button"
          onClick={readOnly ? undefined : () => onChange(star)}
          cursor={readOnly ? "default" : "pointer"}
          color={star <= value ? "#E2B100" : "portal.line"}
          _hover={readOnly ? {} : { color: "#C99A00" }}
        >
          {star <= value ? <FaStar size={16} /> : <FaRegStar size={16} />}
        </Box>
      ))}
    </HStack>
  );
};

const ConsultantFollowUpTab = ({ consultantId, children }) => {
  const { t, i18n } = useTranslation("portal");
  const { selectedBusinessUnitId, effectiveRole } = useBusinessUnit();
  const toast = useToast();
  const monthNames = i18n.language?.startsWith("fr") ? MONTH_NAMES_FR : MONTH_NAMES_EN;

  // BU Manager is admin-equivalent (same as BU Admin / Company Admin).
  // Non-admins can create/edit ratings and reviews but cannot delete them.
  const canDelete = useMemo(() => {
    return effectiveRole === PLATFORM_ROLES.COMPANY_ADMIN ||
           effectiveRole === BU_ROLES.ADMIN ||
           effectiveRole === BU_ROLES.MANAGER;
  }, [effectiveRole]);

  // Monthly ratings state
  const [ratings, setRatings] = useState([]);
  const [loadingRatings, setLoadingRatings] = useState(false);
  const ratingModal = useDisclosure();
  const [editingRating, setEditingRating] = useState(null);
  const [ratingForm, setRatingForm] = useState({ periodYear: new Date().getFullYear(), periodMonth: new Date().getMonth() + 1, rating: 0, comment: "" });
  const [savingRating, setSavingRating] = useState(false);

  // Annual reviews state
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const reviewModal = useDisclosure();
  const [editingReview, setEditingReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({ reviewYear: new Date().getFullYear(), reviewType: "annual", notes: "" });
  const [savingReview, setSavingReview] = useState(false);

  // Delete confirm
  const deleteConfirm = useDisclosure();
  const [deleteTarget, setDeleteTarget] = useState(null);

  const buOpts = { buId: selectedBusinessUnitId || undefined };

  const fetchRatings = useCallback(async () => {
    if (!consultantId) return;
    setLoadingRatings(true);
    try {
      const data = await listMonthlyRatings(consultantId, buOpts);
      setRatings(Array.isArray(data) ? data : []);
    } catch {
      setRatings([]);
    } finally {
      setLoadingRatings(false);
    }
  }, [consultantId, selectedBusinessUnitId]);

  const fetchReviews = useCallback(async () => {
    if (!consultantId) return;
    setLoadingReviews(true);
    try {
      const data = await listAnnualReviews(consultantId, buOpts);
      setReviews(Array.isArray(data) ? data : []);
    } catch {
      setReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  }, [consultantId, selectedBusinessUnitId]);

  useEffect(() => {
    fetchRatings();
    fetchReviews();
  }, [fetchRatings, fetchReviews]);

  // Rating CRUD
  const openAddRating = () => {
    setEditingRating(null);
    setRatingForm({ periodYear: new Date().getFullYear(), periodMonth: new Date().getMonth() + 1, rating: 0, comment: "" });
    ratingModal.onOpen();
  };

  const openEditRating = (r) => {
    setEditingRating(r);
    setRatingForm({ periodYear: r.periodYear, periodMonth: r.periodMonth, rating: r.rating, comment: r.comment || "" });
    ratingModal.onOpen();
  };

  const handleSaveRating = async () => {
    if (ratingForm.rating < 1 || ratingForm.rating > 5) {
      toast({ title: t("pilotConsultants.detail.followUp.monthlyRatings.selectRating", { defaultValue: "Please select a rating" }), status: "warning" });
      return;
    }
    setSavingRating(true);
    try {
      if (editingRating) {
        await updateMonthlyRating(consultantId, editingRating.id, { rating: ratingForm.rating, comment: ratingForm.comment || null }, buOpts);
      } else {
        await createMonthlyRating(consultantId, ratingForm, buOpts);
      }
      ratingModal.onClose();
      fetchRatings();
    } catch {
      toast({ title: t("common.error", { defaultValue: "Error" }), status: "error" });
    } finally {
      setSavingRating(false);
    }
  };

  // Review CRUD
  const openAddReview = () => {
    setEditingReview(null);
    setReviewForm({ reviewYear: new Date().getFullYear(), reviewType: "annual", notes: "" });
    reviewModal.onOpen();
  };

  const openEditReview = (r) => {
    setEditingReview(r);
    setReviewForm({ reviewYear: r.reviewYear, reviewType: r.reviewType, notes: r.notes || "" });
    reviewModal.onOpen();
  };

  const handleSaveReview = async () => {
    setSavingReview(true);
    try {
      if (editingReview) {
        await updateAnnualReview(consultantId, editingReview.id, reviewForm, buOpts);
      } else {
        await createAnnualReview(consultantId, reviewForm, buOpts);
      }
      reviewModal.onClose();
      fetchReviews();
    } catch {
      toast({ title: t("common.error", { defaultValue: "Error" }), status: "error" });
    } finally {
      setSavingReview(false);
    }
  };

  // Delete handler
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === "rating") {
        await deleteMonthlyRating(consultantId, deleteTarget.id, buOpts);
        fetchRatings();
      } else {
        await deleteAnnualReview(consultantId, deleteTarget.id, buOpts);
        fetchReviews();
      }
    } catch {
      toast({ title: t("common.error", { defaultValue: "Error" }), status: "error" });
    } finally {
      setDeleteTarget(null);
      deleteConfirm.onClose();
    }
  };

  const reviewTypeLabel = (type) => {
    return t(`pilotConsultants.detail.followUp.annualReviews.types.${type}`, { defaultValue: type });
  };

  const ratingColumns = [
    { key: "period", label: t("pilotConsultants.detail.followUp.monthlyRatings.period"), width: "22%" },
    { key: "rating", label: t("pilotConsultants.detail.followUp.monthlyRatings.rating"), width: "26%" },
    { key: "comment", label: t("pilotConsultants.detail.followUp.monthlyRatings.comment") },
    { key: "actions", label: "", width: "78px", align: "right" },
  ];

  const reviewColumns = [
    { key: "year", label: t("pilotConsultants.detail.followUp.annualReviews.year"), width: "12%" },
    { key: "type", label: t("pilotConsultants.detail.followUp.annualReviews.type"), width: "20%" },
    { key: "notes", label: t("pilotConsultants.detail.followUp.annualReviews.notes") },
    { key: "reviewer", label: t("pilotConsultants.detail.followUp.annualReviews.reviewer"), width: "18%" },
    { key: "actions", label: "", width: "78px", align: "right" },
  ];

  const addRatingBtn = (
    <Button size="sm" leftIcon={<AddIcon />} variant="primary" onClick={openAddRating}>
      {t("pilotConsultants.detail.followUp.monthlyRatings.add")}
    </Button>
  );

  const addReviewBtn = (
    <Button size="sm" leftIcon={<AddIcon />} variant="primary" onClick={openAddReview}>
      {t("pilotConsultants.detail.followUp.annualReviews.add")}
    </Button>
  );

  return (
    <VStack align="stretch" spacing="10px">
      {/* Existing self-service content (portal access, activity reports, leave requests, feedback) */}
      {children}

      {/* Monthly Ratings */}
      <Panel
        title={t("pilotConsultants.detail.followUp.monthlyRatings.title")}
        count={loadingRatings ? undefined : ratings.length}
        actions={addRatingBtn}
      >
        <DataTable
          columns={ratingColumns}
          rows={ratings}
          loading={loadingRatings}
          emptyState={
            <EmptyState
              icon={<FaStar />}
              title={t("pilotConsultants.detail.followUp.monthlyRatings.noRatings")}
            />
          }
          renderRow={(r) => (
            <Tr key={r.id}>
              <Td fontWeight="700">{monthNames[(r.periodMonth || 1) - 1]} {r.periodYear}</Td>
              <Td><StarRating value={r.rating} readOnly /></Td>
              <Td>{r.comment || "—"}</Td>
              <Td textAlign="right">
                <HStack spacing="6px" justify="flex-end">
                  <MiniIconButton
                    icon={<EditIcon />}
                    aria-label={t("pilotConsultants.detail.conversations.actions.edit", { defaultValue: "Edit" })}
                    onClick={() => openEditRating(r)}
                  />
                  <MiniIconButton
                    icon={<DeleteIcon />}
                    aria-label={t("pilotConsultants.detail.conversations.actions.delete", { defaultValue: "Delete" })}
                    color="portal.danger"
                    _hover={{ bg: "danger.50", borderColor: "portal.danger", color: "portal.danger" }}
                    isDisabled={!canDelete}
                    onClick={() => { setDeleteTarget({ type: "rating", id: r.id }); deleteConfirm.onOpen(); }}
                  />
                </HStack>
              </Td>
            </Tr>
          )}
        />
      </Panel>

      {/* Annual Reviews */}
      <Panel
        title={t("pilotConsultants.detail.followUp.annualReviews.title")}
        count={loadingReviews ? undefined : reviews.length}
        actions={addReviewBtn}
      >
        <DataTable
          columns={reviewColumns}
          rows={reviews}
          loading={loadingReviews}
          emptyState={
            <EmptyState
              icon={<FaRegStickyNote />}
              title={t("pilotConsultants.detail.followUp.annualReviews.noReviews")}
            />
          }
          renderRow={(r) => (
            <Tr key={r.id}>
              <Td fontWeight="700">{r.reviewYear}</Td>
              <Td>
                <StatusBadge
                  label={reviewTypeLabel(r.reviewType)}
                  colorScheme={REVIEW_TYPE_SCHEME[r.reviewType] || "gray"}
                />
              </Td>
              <Td>{r.notes || "—"}</Td>
              <Td>{r.reviewerName || "—"}</Td>
              <Td textAlign="right">
                <HStack spacing="6px" justify="flex-end">
                  <MiniIconButton
                    icon={<EditIcon />}
                    aria-label={t("pilotConsultants.detail.conversations.actions.edit", { defaultValue: "Edit" })}
                    onClick={() => openEditReview(r)}
                  />
                  <MiniIconButton
                    icon={<DeleteIcon />}
                    aria-label={t("pilotConsultants.detail.conversations.actions.delete", { defaultValue: "Delete" })}
                    color="portal.danger"
                    _hover={{ bg: "danger.50", borderColor: "portal.danger", color: "portal.danger" }}
                    isDisabled={!canDelete}
                    onClick={() => { setDeleteTarget({ type: "review", id: r.id }); deleteConfirm.onOpen(); }}
                  />
                </HStack>
              </Td>
            </Tr>
          )}
        />
      </Panel>

      {/* Special Requests — Coming Soon */}
      <Panel title={t("pilotConsultants.detail.followUp.specialRequests.title")}>
        <Box p="12px">
          <EmptyState
            icon={<FaRegCommentDots />}
            title={t("pilotConsultants.detail.followUp.specialRequests.comingSoon")}
          />
        </Box>
      </Panel>

      {/* Monthly Rating Modal */}
      <Modal isOpen={ratingModal.isOpen} onClose={ratingModal.onClose} size="md">
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            <Heading as="h2" fontSize="17px" fontWeight={800} letterSpacing="-0.02em" color="portal.text" sx={{ marginBottom: "0 !important" }}>
              {editingRating
                ? t("pilotConsultants.detail.followUp.monthlyRatings.editTitle", { defaultValue: "Edit rating" })
                : t("pilotConsultants.detail.followUp.monthlyRatings.add")}
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="16px">
            <VStack spacing="14px" align="stretch">
              <HStack spacing="12px" align="flex-start">
                <FormControl>
                  <FormLabel mb="4px"><FieldLabel as="span">{t("pilotConsultants.detail.followUp.monthlyRatings.periodYear")}</FieldLabel></FormLabel>
                  <Input
                    h="31px"
                    fontWeight="600"
                    type="number"
                    value={ratingForm.periodYear}
                    onChange={(e) => setRatingForm({ ...ratingForm, periodYear: parseInt(e.target.value) || new Date().getFullYear() })}
                    isDisabled={!!editingRating}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel mb="4px"><FieldLabel as="span">{t("pilotConsultants.detail.followUp.monthlyRatings.periodMonth")}</FieldLabel></FormLabel>
                  <Select
                    h="31px"
                    fontWeight="600"
                    value={ratingForm.periodMonth}
                    onChange={(e) => setRatingForm({ ...ratingForm, periodMonth: parseInt(e.target.value) })}
                    isDisabled={!!editingRating}
                  >
                    {MONTHS.map((m) => (
                      <option key={m} value={m}>{monthNames[m - 1]}</option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel mb="4px"><FieldLabel as="span">{t("pilotConsultants.detail.followUp.monthlyRatings.rating")}</FieldLabel></FormLabel>
                <StarRating value={ratingForm.rating} onChange={(v) => setRatingForm({ ...ratingForm, rating: v })} />
              </FormControl>
              <FormControl>
                <FormLabel mb="4px"><FieldLabel as="span">{t("pilotConsultants.detail.followUp.monthlyRatings.comment")}</FieldLabel></FormLabel>
                <Textarea
                  fontWeight="600"
                  value={ratingForm.comment}
                  onChange={(e) => setRatingForm({ ...ratingForm, comment: e.target.value })}
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" onClick={ratingModal.onClose}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button variant="primary" size="sm" onClick={handleSaveRating} isLoading={savingRating}>
              {t("pilotConsultants.detail.followUp.monthlyRatings.save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Annual Review Modal */}
      <Modal isOpen={reviewModal.isOpen} onClose={reviewModal.onClose} size="md">
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            <Heading as="h2" fontSize="17px" fontWeight={800} letterSpacing="-0.02em" color="portal.text" sx={{ marginBottom: "0 !important" }}>
              {editingReview
                ? t("pilotConsultants.detail.followUp.annualReviews.editTitle", { defaultValue: "Edit review" })
                : t("pilotConsultants.detail.followUp.annualReviews.add")}
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="16px">
            <VStack spacing="14px" align="stretch">
              <HStack spacing="12px" align="flex-start">
                <FormControl>
                  <FormLabel mb="4px"><FieldLabel as="span">{t("pilotConsultants.detail.followUp.annualReviews.year")}</FieldLabel></FormLabel>
                  <Input
                    h="31px"
                    fontWeight="600"
                    type="number"
                    value={reviewForm.reviewYear}
                    onChange={(e) => setReviewForm({ ...reviewForm, reviewYear: parseInt(e.target.value) || new Date().getFullYear() })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel mb="4px"><FieldLabel as="span">{t("pilotConsultants.detail.followUp.annualReviews.type")}</FieldLabel></FormLabel>
                  <Select
                    h="31px"
                    fontWeight="600"
                    value={reviewForm.reviewType}
                    onChange={(e) => setReviewForm({ ...reviewForm, reviewType: e.target.value })}
                  >
                    {REVIEW_TYPES.map((rt) => (
                      <option key={rt} value={rt}>{reviewTypeLabel(rt)}</option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>
              <FormControl>
                <FormLabel mb="4px"><FieldLabel as="span">{t("pilotConsultants.detail.followUp.annualReviews.notes")}</FieldLabel></FormLabel>
                <Textarea
                  fontWeight="600"
                  value={reviewForm.notes}
                  onChange={(e) => setReviewForm({ ...reviewForm, notes: e.target.value })}
                  rows={4}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" onClick={reviewModal.onClose}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button variant="primary" size="sm" onClick={handleSaveReview} isLoading={savingReview}>
              {t("pilotConsultants.detail.followUp.annualReviews.save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => { setDeleteTarget(null); deleteConfirm.onClose(); }}
        onConfirm={handleConfirmDelete}
        title={t("common.confirmDelete", { defaultValue: "Confirm Delete" })}
        body={t("common.confirmDeleteMessage", { defaultValue: "Are you sure you want to delete this item?" })}
      />
    </VStack>
  );
};

export default ConsultantFollowUpTab;
